import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, ArrowUpDown, AlertTriangle, FilePlus } from 'lucide-react';
import { supabase } from '../services/supabase';
import CreateEvaluationModal from './CreateEvaluationModal';
import EvaluationForm from './EvaluationForm';

// Entrega 1 del seguimiento de evaluaciones (mig 194) — tabla de dirección con
// TODOS los alumnos activos y su contador de sesiones desde el último informe.
//
// Por qué existe: el único contador que había vivía dentro de
// `formal_evaluations_pending_for_trainer`, con el umbral 8 clavado en el WHERE.
// Sus 6 consumidores muestran esa cola filtrada, así que el alumno que va por 7
// no aparecía en ninguna pantalla. Esta tabla enseña a todos, sin umbral, para
// que dirección decida a mano a quién adelantar el informe.
//
// El contador es CLUB-WIDE (una fila por alumno: cualquier sesión asistida,
// último informe sea de quien sea).
//
// ⚠️ CORREGIDO POR LA MIG 319. Aquí decía que este número «puede ser MENOR que
// el que ve un entrenador concreto, cuyo contador es por pareja (alumno,
// entrenador)», y que no era una discrepancia sino dos preguntas distintas. Eso
// dejó de ser verdad: la 319 hizo que el contador de la cola del entrenador
// también sea del ALUMNO, porque un chaval repartido 7 y 7 llevaba catorce
// sesiones sin evaluar y no salía en la cola de nadie. Ahora esta tabla y la
// pantalla del entrenador enseñan EL MISMO número, y si algún día no coinciden
// sí es un fallo.
//
// ENTREGA 2: el botón "Crear informe" por fila. Dirección elige entrenador y
// periodo en un diálogo y entra al MISMO formulario que usa el entrenador
// (`EvaluationForm`), que ahora guarda borradores. El informe lo firma el
// entrenador elegido; `created_by` guarda quién lo tecleó, solo para auditoría.

// ⚠️ AQUÍ HABÍA UNA SEGUNDA COPIA DEL UMBRAL (`const UMBRAL = 8`), y la mig 320
// la quitó. El número llega ahora en la columna `umbral` de la RPC, que lo saca
// de `umbral_de_evaluacion()`: el único sitio del producto donde está escrito.
// Y quién lo pasa no se calcula aquí, viene en `pasa_umbral` — así esta tabla y
// la cola del entrenador no pueden discrepar.
//
// Esto SÍ es una decisión de presentación y se queda: cuántas sesiones antes del
// umbral se empieza a resaltar en ámbar. No es el umbral.
const MARGEN_CERCA = 2;

interface CounterRow {
  student_id: string;
  student_name: string;
  sessions_since_last: number;
  last_eval_date: string | null;
  last_period_end: string | null;
  last_report_number: string | null;
  last_monitor_id: string | null;
  last_monitor_name: string | null;
  total_evaluations: number;
  suggested_period_start: string | null;
  suggested_period_end: string | null;
  pasa_umbral: boolean; // mig 320 · lo decide el SQL, no esta pantalla
  umbral: number; // mig 320 · para PINTAR el número sin conocerlo
}

type SortKey = 'sessions' | 'name' | 'last_eval';
type SortDir = 'asc' | 'desc';

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Rango del bloque sin evaluar. Con 0 sesiones no hay bloque.
function fmtPeriodo(from: string | null, to: string | null): string {
  if (!from || !to) return '—';
  return `${fmtDate(from)} → ${fmtDate(to)}`;
}

/** Alumno elegido en la tabla, a la espera de entrenador y periodo. */
interface Candidate {
  studentId: string;
  studentName: string;
  suggestedStart: string | null;
  suggestedEnd: string | null;
}

/** Informe abierto en el formulario. */
interface ActiveForm extends Candidate {
  monitorId: string;
  monitorName: string;
  periodStart: string;
  periodEnd: string;
}

export default function EvaluationTrackingTable() {
  const [rows, setRows] = useState<CounterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('sessions');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [active, setActive] = useState<ActiveForm | null>(null);
  // Lo último asignado. El diálogo se cierra al asignar, así que sin esto la
  // pantalla no diría en ninguna parte que el informe ya está en manos de
  // alguien: la fila de la tabla no cambia (el contador es de sesiones).
  const [asignado, setAsignado] = useState<{
    studentName: string;
    monitorName: string;
    periodStart: string;
    periodEnd: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('evaluation_counters_all_students');
    if (error) {
      // La RPC es admin/director-only (42501 para el resto). No dejamos la
      // tabla en blanco sin explicación.
      setErrMsg(error.message ?? 'No se pudo cargar el seguimiento de evaluaciones');
      setRows([]);
    } else {
      setErrMsg('');
      setRows((data ?? []) as unknown as CounterRow[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Por defecto: nombres A→Z, números y fechas de mayor a menor.
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => r.student_name.toLowerCase().includes(q))
      : rows.slice();

    const mult = sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      if (sortKey === 'name') return mult * a.student_name.localeCompare(b.student_name, 'es');
      if (sortKey === 'sessions') {
        const d = a.sessions_since_last - b.sessions_since_last;
        // Empate por contador → nombre, para que el orden sea estable.
        return d !== 0 ? mult * d : a.student_name.localeCompare(b.student_name, 'es');
      }
      // last_eval: los que nunca han sido evaluados van al final en desc y al
      // principio en asc (son el caso que más interesa mirar).
      const av = a.last_eval_date ?? '';
      const bv = b.last_eval_date ?? '';
      if (av === bv) return a.student_name.localeCompare(b.student_name, 'es');
      return mult * av.localeCompare(bv);
    });
  }, [rows, search, sortKey, sortDir]);

  const resumen = useMemo(
    () => ({
      total: rows.length,
      tocan: rows.filter((r) => r.pasa_umbral).length,
      cerca: rows.filter(
        (r) => !r.pasa_umbral && r.sessions_since_last >= r.umbral - MARGEN_CERCA
      ).length,
      nunca: rows.filter((r) => r.total_evaluations === 0).length,
    }),
    [rows]
  );

  // mig 320 · El número que se PINTA sale del SQL, igual para todas las filas.
  // Sin filas no hay número que pintar y la frase se escribe sin él.
  const umbral = rows.length ? rows[0].umbral : null;

  // El formulario sustituye a la tabla, igual que en la pantalla del entrenador.
  if (active) {
    return (
      <EvaluationForm
        studentId={active.studentId}
        studentName={active.studentName}
        monitorId={active.monitorId}
        periodStart={active.periodStart}
        periodEnd={active.periodEnd}
        title={`Informe · firma ${active.monitorName}`}
        onBack={() => {
          setActive(null);
          load();
        }}
        onSaved={load}
      />
    );
  }

  if (loading)
    return (
      <p className="text-gold-primary font-oswald animate-pulse p-4" data-testid="tracking-loading">
        Cargando...
      </p>
    );

  if (errMsg)
    return (
      <p className="text-error font-inter text-sm p-4" data-testid="tracking-error">
        {errMsg}
      </p>
    );

  return (
    <div className="space-y-4" data-testid="panel-seguimiento">
      <p className="text-text-secondary font-inter text-sm">
        Sesiones asistidas por cada alumno desde su último informe formal, contando las de{' '}
        <strong className="text-white">cualquier entrenador</strong>.{' '}
        {umbral === null
          ? 'El umbral marca a quién le toca, pero no impide adelantar un informe.'
          : `El umbral de ${umbral} marca a quién le toca, pero no impide adelantar un informe.`}
      </p>

      {/* ── Lo último asignado ──
          Dice las tres cosas que dirección querría poder comprobar después:
          a quién, de quién y qué periodo. Se queda hasta que se asigne otro o
          se salga de la pestaña; no es una alerta, es un acuse. */}
      {asignado && (
        <div
          className="border border-gold-primary rounded-lg p-3 flex items-start justify-between gap-3"
          data-testid="tracking-asignado"
        >
          <p className="font-inter text-sm text-white">
            Informe de <strong>{asignado.studentName}</strong> asignado a{' '}
            <strong>{asignado.monitorName}</strong> ({fmtDate(asignado.periodStart)} →{' '}
            {fmtDate(asignado.periodEnd)}). Ya está en su lista de pendientes.
          </p>
          <button
            onClick={() => setAsignado(null)}
            aria-label="cerrar aviso"
            data-testid="tracking-asignado-cerrar"
            className="text-text-secondary font-inter text-xs shrink-0"
          >
            Cerrar
          </button>
        </div>
      )}

      {/* ── Resumen ── */}
      <div className="flex flex-wrap gap-2" data-testid="tracking-resumen">
        <span className="bg-bg-surface border border-bg-border rounded-lg px-3 py-1.5 font-inter text-xs text-text-secondary">
          {resumen.total} alumnos activos
        </span>
        <span className="bg-bg-surface border border-gold-primary rounded-lg px-3 py-1.5 font-inter text-xs text-gold-primary">
          {`${resumen.tocan} le(s) toca${umbral === null ? '' : ` (${umbral}+)`}`}
        </span>
        <span className="bg-bg-surface border border-warning rounded-lg px-3 py-1.5 font-inter text-xs text-warning">
          {`${resumen.cerca} cerca${
            umbral === null ? '' : ` (${umbral - MARGEN_CERCA}-${umbral - 1})`
          }`}
        </span>
        <span className="bg-bg-surface border border-bg-border rounded-lg px-3 py-1.5 font-inter text-xs text-text-secondary">
          {resumen.nunca} sin ningún informe
        </span>
      </div>

      {/* ── Buscador ── */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 text-text-secondary" size={18} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar alumno..."
          data-testid="tracking-search"
          className="w-full bg-bg-surface border border-bg-border rounded-lg pl-10 pr-3 py-2 text-white text-sm"
        />
      </div>

      {/* ── Tabla ── */}
      {visible.length === 0 ? (
        <p className="text-text-secondary font-inter text-sm" data-testid="tracking-empty">
          {rows.length === 0
            ? 'No hay alumnos activos.'
            : 'Ningún alumno coincide con la búsqueda.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tracking-table">
            <thead>
              <tr className="text-text-secondary font-inter text-xs border-b border-bg-border">
                <th className="text-left py-2 px-2">
                  <button
                    onClick={() => toggleSort('name')}
                    data-testid="sort-name"
                    className="flex items-center gap-1 hover:text-white"
                  >
                    Alumno <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-right py-2 px-2">
                  <button
                    onClick={() => toggleSort('sessions')}
                    data-testid="sort-sessions"
                    className="flex items-center gap-1 ml-auto hover:text-white"
                  >
                    Sesiones desde el último <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-left py-2 px-2">
                  <button
                    onClick={() => toggleSort('last_eval')}
                    data-testid="sort-last-eval"
                    className="flex items-center gap-1 hover:text-white"
                  >
                    Último informe <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-left py-2 px-2">Entrenador</th>
                <th className="text-left py-2 px-2">Periodo sin evaluar</th>
                <th className="text-right py-2 px-2">Informes</th>
                <th className="text-right py-2 px-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const toca = r.pasa_umbral;
                const cerca = !toca && r.sessions_since_last >= r.umbral - MARGEN_CERCA;
                // Un informe con period_end NULL no reinicia el contador (ni
                // con el entrenador correcto). Se avisa para que el número no
                // parezca un error.
                const sinCierre = r.total_evaluations > 0 && !r.last_period_end;
                return (
                  <tr
                    key={r.student_id}
                    data-testid={`tracking-row-${r.student_id}`}
                    className="border-b border-bg-border/50"
                  >
                    <td className="py-2 px-2 font-oswald text-white">{r.student_name}</td>
                    <td className="py-2 px-2 text-right">
                      <span
                        data-testid={`tracking-count-${r.student_id}`}
                        className={`font-oswald tabular-nums px-2 py-0.5 rounded ${
                          toca
                            ? 'bg-gold-primary text-black'
                            : cerca
                              ? 'text-warning'
                              : 'text-text-secondary'
                        }`}
                      >
                        {r.sessions_since_last}
                      </span>
                    </td>
                    <td className="py-2 px-2 font-inter text-text-secondary">
                      {r.total_evaluations === 0 ? (
                        <span className="text-text-secondary">Nunca evaluado</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          {fmtDate(r.last_eval_date)}
                          {r.last_report_number && (
                            <span className="text-xs opacity-70">{r.last_report_number}</span>
                          )}
                          {sinCierre && (
                            <span
                              title="El informe no tiene fecha de cierre de periodo, así que no reinicia el contador"
                              data-testid={`tracking-sin-cierre-${r.student_id}`}
                              className="text-warning inline-flex"
                            >
                              <AlertTriangle size={13} />
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 font-inter text-text-secondary">
                      {r.last_monitor_name ?? '—'}
                    </td>
                    <td className="py-2 px-2 font-inter text-text-secondary text-xs">
                      {fmtPeriodo(r.suggested_period_start, r.suggested_period_end)}
                    </td>
                    <td className="py-2 px-2 text-right font-inter text-text-secondary tabular-nums">
                      {r.total_evaluations}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <button
                        onClick={() =>
                          setCandidate({
                            studentId: r.student_id,
                            studentName: r.student_name,
                            suggestedStart: r.suggested_period_start,
                            suggestedEnd: r.suggested_period_end,
                          })
                        }
                        data-testid={`crear-informe-${r.student_id}`}
                        className="inline-flex items-center gap-1.5 bg-bg-elevated border border-bg-border hover:border-gold-primary text-white font-inter text-xs rounded-lg px-3 py-1.5 whitespace-nowrap"
                      >
                        <FilePlus size={14} /> Crear informe
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {candidate && (
        <CreateEvaluationModal
          studentId={candidate.studentId}
          studentName={candidate.studentName}
          suggestedStart={candidate.suggestedStart}
          suggestedEnd={candidate.suggestedEnd}
          onCancel={() => setCandidate(null)}
          onConfirm={({ monitorId, monitorName, periodStart, periodEnd }) => {
            setActive({ ...candidate, monitorId, monitorName, periodStart, periodEnd });
            setCandidate(null);
          }}
          onAssigned={({ monitorName, periodStart, periodEnd }) => {
            setAsignado({
              studentName: candidate.studentName,
              monitorName,
              periodStart,
              periodEnd,
            });
            setCandidate(null);
            // El seguimiento no cambia con una asignación —el contador sigue
            // siendo de sesiones— pero sí la lista de borradores abiertos que
            // el diálogo enseña la próxima vez.
            load();
          }}
        />
      )}
    </div>
  );
}
